import { Component, OnDestroy, OnInit } from "@angular/core";
import { doc, Firestore, getDoc } from "@angular/fire/firestore";
import { MatIcon } from "@angular/material/icon";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { LoadingService } from "@core/loading.service";
import { IonicModule, NavController } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { from, fromEvent, of, Subject } from "rxjs";
import { catchError, filter, map, take, takeUntil } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import { CommonService } from "src/app/services/common/common.service";

const FALLBACK_TYPEFORM_URLS: { [key: string]: string } = {
  lottery: "https://form.typeform.com/to/ROoo4y9Y",
  games: "https://form.typeform.com/to/HKLQsMIQ",
  support: "https://form.typeform.com/to/VuRyoHWp",
  social: "https://form.typeform.com/to/dp909Ihe",
};

const ALLOWED_TYPEFORM_HOSTS = new Set(["form.typeform.com", "embed.typeform.com"]);
const ALLOWED_DEST_HOSTS = new Set(["support.lottosocial.com"]);
const TYPEFORM_PATH_PATTERN = /^\/to\/[A-Za-z0-9_-]+\/?$/;

@Component({
  selector: "app-suggestion",
  standalone: true,
  imports: [IonicModule, MatIcon, TranslatePipe],
  templateUrl: "./suggestion.component.html",
  styleUrl: "./suggestion.component.scss",
})
export class SuggestionComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();
  public selectedLanguage: string;
  private userDetails: Data;
  public userId: string;
  private typeformPopup: { open: () => void; close: () => void } | null = null;

  public typeFormData: { [key: string]: string } = { ...FALLBACK_TYPEFORM_URLS };

  constructor(
    private navCtrl: NavController,
    public translate: TranslateService,
    private authService: AuthService,
    private loadingService: LoadingService,
    private firestore: Firestore,
    private commonService: CommonService,
    private router: Router,
  ) { }

  async ngOnInit() {
    await this.authService.getUid("SuggestionComponent").then((uid) => {
      this.userId = uid;
    })
    // This method has been used to set the language
    try {
      this.authService?.selectedlanguage$?.pipe(takeUntil(this.unsubscribe$))?.subscribe({
        next: (language) => {
          this.selectedLanguage = language ? language : CommonVariable?.preferredLanguage;
          if (this.selectedLanguage) {
            this.translate.use(this.selectedLanguage);
          }
        },
        error: (err) => {
          return err;
        },
      });
      this.getUserDetails();
      this.loadTypeFormSurveyUrls();
      this.listenForTypeformEnding();
    } catch (err) {
      return err;
    }
  }

  /**
   * Loads survey URLs from /Global_lookup/typeform_surveys.
   * Keeps in-bundle fallbacks when the document is missing, malformed, or slow.
   */
  private loadTypeFormSurveyUrls(): void {
    this.refreshTypeFormSurveyUrls$()
      .pipe(take(1), takeUntil(this.unsubscribe$))
      .subscribe();
  }

  /**
   * Re-reads /Global_lookup/typeform_surveys so a Firestore edit is picked up
   * on the next card open without a rebuild or release (AC2).
   */
  private refreshTypeFormSurveyUrls$() {
    const docRef = doc(this.firestore, "/Global_lookup/typeform_surveys");
    return from(getDoc(docRef)).pipe(
      map((snapshot) => {
        if (!snapshot?.exists()) {
          return;
        }
        const data = snapshot.data() as { [key: string]: unknown };
        if (!data || typeof data !== "object") {
          return;
        }
        const nextUrls = { ...FALLBACK_TYPEFORM_URLS };
        Object.keys(FALLBACK_TYPEFORM_URLS).forEach((key) => {
          const value = data[key];
          if (typeof value === "string" && this.isAllowedTypeformSurveyUrl(value)) {
            nextUrls[key] = value.trim();
          }
        });
        this.typeFormData = nextUrls;
      }),
      catchError(() => of(undefined)),
    );
  }

  /** Accept only HTTPS Typeform form URLs from Firestore (AC2 + hidden-field safety). */
  private isAllowedTypeformSurveyUrl(value: string): boolean {
    try {
      const url = new URL(value.trim());
      if (url.protocol !== "https:") {
        return false;
      }
      if (!ALLOWED_TYPEFORM_HOSTS.has(url.hostname)) {
        return false;
      }
      return TYPEFORM_PATH_PATTERN.test(url.pathname);
    } catch {
      return false;
    }
  }

  private resolveSurveyKey(url: string): string | null {
    const fromCurrent = Object.keys(this.typeFormData).find((key) => this.typeFormData[key] === url);
    if (fromCurrent) {
      return fromCurrent;
    }
    return Object.keys(FALLBACK_TYPEFORM_URLS).find((key) => FALLBACK_TYPEFORM_URLS[key] === url) ?? null;
  }

  getUserDetails() {
    this.authService.userDetails$.pipe(take(1)).subscribe({
      next: (data: Data) => {
        this.userDetails = data;
      },
      error: (error) => {
        return error;
      },
    });
  }

  openTypeForm(url: string) {
    const surveyKey = this.resolveSurveyKey(url);
    this.commonService.getUid("openTypeForm").pipe(take(1)).subscribe({
      next: (uid) => {
        if (!uid) {
          console.warn("User ID not found, cannot open type form.");
          return;
        }
        this.refreshTypeFormSurveyUrls$().pipe(take(1)).subscribe({
          next: () => {
            const surveyUrl = surveyKey ? this.typeFormData[surveyKey] : url;
            const docRef = doc(this.firestore, `/users/${uid}/balance/current`);
            from(getDoc(docRef))
              .pipe(
                map(snapshot => snapshot.data()),
                take(1)
              )
              .subscribe({
                next: (balance: any) => {
                  if (balance) {
                    const credit_balance = balance?.preferred_currency?.credit ?? null;
                    const point_balance = balance?.points ?? null;
                    const startTimestamp = this.userDetails?.timestamp?.seconds;
                    const endTimestamp = Date.now();
                    const difference = this.getTimeDifference(startTimestamp, endTimestamp);

                    const hiddenData = {
                      uid,
                      user_segment: this.userDetails?.pwa_segment_status,
                      service_age: difference,
                      points_balance: point_balance,
                      credit_balance: credit_balance,
                    };

                    const params = new URLSearchParams(hiddenData).toString();
                    const finalUrl = `${surveyUrl}#${params}`;

                    this.typeForm(finalUrl, hiddenData);
                  }
                },
                error: (error) => console.error("Error fetching balance:", error)
              });
          },
        });
      },
      error: (err) => console.error("Error fetching UID from commonService:", err)
    });
  }


  getTimeDifference(startTimestamp, endTimestamp) {
    const start = new Date(startTimestamp * 1000); // Convert to milliseconds
    const end = new Date(endTimestamp); // Convert to milliseconds

    // Initial difference in each unit
    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();
    let hours = end.getHours() - start.getHours();
    let minutes = end.getMinutes() - start.getMinutes();
    let seconds = end.getSeconds() - start.getSeconds();

    // Adjust for negative seconds, minutes, hours
    if (seconds < 0) {
      seconds += 60;
      minutes--;
    }
    if (minutes < 0) {
      minutes += 60;
      hours--;
    }
    if (hours < 0) {
      hours += 24;
      days--;
    }

    // Adjust for negative days
    if (days < 0) {
      const previousMonth = new Date(end.getFullYear(), end.getMonth(), 0); // Last day of the previous month
      days += previousMonth.getDate();
      months--;
    }

    // Adjust for negative months
    if (months < 0) {
      months += 12;
      years--;
    }

    // Filter out units that are zero
    const result = {
      years,
      months,
      days,
      hours,
      minutes,
      seconds,
    };

    return Object.entries(result)
      .filter(([_, value]) => value !== 0)
      .map(([unit, value]) => `${value} ${unit}`)
      .join(", ");
  }

  typeForm(surveyLink: string, hiddenData: Data): void {
    this.loadingService.present();
    try {
      this.typeformPopup = window["typeformEmbed"]
        ?.makePopup(surveyLink, {
          hideHeaders: true,
          hideFooter: true,
          hidden: hiddenData,
          onSubmit: () => {
            // Keep the end screen visible until the user dismisses or a redirect bridge closes it.
          },
          onClose: () => {
            this.typeformPopup = null;
            this.loadingService.dismiss().catch((err) => {
              return err;
            });
          },
          onReady: () => {
            this.loadingService.dismiss().catch((err) => {
              return err;
            });
          },
        }) ?? null;
      this.typeformPopup?.open();
      if (!this.typeformPopup) {
        this.loadingService.dismiss().catch((err) => err);
      }
    } catch (error) {
      this.typeformPopup = null;
      this.loadingService.dismiss();
      return error;
    }
  }

  private closeTypeformPopup(): void {
    try {
      this.typeformPopup?.close();
    } catch {
      // Popup may already be closed by the embed SDK.
    }
    this.typeformPopup = null;
  }

  private listenForTypeformEnding(): void {
    fromEvent<MessageEvent>(window, "message")
      .pipe(
        filter((event) => event.origin === window.location.origin),
        filter((event) => !!event.data && event.data.type === "typeform-ending"),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((event) => {
        const dest = typeof event.data?.dest === "string" ? event.data.dest.trim() : "";
        this.closeTypeformPopup();
        if (!dest) {
          return;
        }
        if (!this.isAllowedDest(dest)) {
          return;
        }
        this.navigateToDest(dest);
      });
  }

  private isAllowedDest(dest: string): boolean {
    try {
      if (dest.startsWith("/") && !dest.startsWith("//")) {
        return true;
      }
      const url = new URL(dest, window.location.origin);
      if (url.origin === window.location.origin) {
        return true;
      }
      if (url.protocol !== "https:") {
        return false;
      }
      return ALLOWED_DEST_HOSTS.has(url.hostname);
    } catch {
      return false;
    }
  }

  private navigateToDest(dest: string): void {
    try {
      if (dest.startsWith("/") && !dest.startsWith("//")) {
        this.router.navigateByUrl(dest);
        return;
      }
      const url = new URL(dest, window.location.origin);
      if (url.origin === window.location.origin) {
        this.router.navigateByUrl(`${url.pathname}${url.search}${url.hash}`);
        return;
      }
      window.open(url.toString(), "_blank", "noopener,noreferrer");
    } catch {
      // Invalid dest ??? popup already closed; stay on suggestions.
    }
  }

  goBack() {
    this.navCtrl.back();
  }

  ngOnDestroy(): void {
    this.closeTypeformPopup();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
